import { create } from 'zustand';
import { contentService } from '../services/contentService';

const useContentStore = create((set, get) => ({
  // State
  contentItems: [],
  loading: false,
  error: null,
  lastUpdated: null,
  subscription: null,

  // Actions
  fetchContent: async (filters = {}) => {
    try {
      set({ loading: true, error: null });
      const data = await contentService?.getAll(filters);
      set({ 
        contentItems: data, 
        loading: false,
        lastUpdated: Date.now()
      });
      return data;
    } catch (error) {
      set({ error: error?.message, loading: false });
      throw error;
    }
  },

  // Update single item (for thumbnail changes)
  updateContentItem: (id, updates) => {
    set((state) => ({
      contentItems: state?.contentItems?.map(item => 
        item?.id === id ? { ...item, ...updates } : item
      ),
      lastUpdated: Date.now()
    }));
  },

  // Add new item
  addContentItem: (newItem) => {
    set((state) => ({
      contentItems: [newItem, ...state?.contentItems],
      lastUpdated: Date.now()
    }));
  },

  // Remove item
  removeContentItem: (id) => {
    set((state) => ({
      contentItems: state?.contentItems?.filter(item => item?.id !== id),
      lastUpdated: Date.now()
    }));
  },

  // Get single item by ID
  getContentById: (id) => {
    return get()?.contentItems?.find(item => item?.id === id);
  },

  // Subscribe to real-time changes
  subscribeToChanges: () => {
    const { subscription } = get();
    
    // Prevent duplicate subscriptions
    if (subscription) {
      return;
    }

    const unsubscribe = contentService?.subscribeToChanges((payload) => {
      const { eventType, new: newRecord, old: oldRecord } = payload;

      if (eventType === 'INSERT') {
        get()?.addContentItem(newRecord);
      } else if (eventType === 'UPDATE') {
        // Critical: Update thumbnail immediately when changed
        get()?.updateContentItem(newRecord?.id, newRecord);
      } else if (eventType === 'DELETE') {
        get()?.removeContentItem(oldRecord?.id);
      }
    });

    set({ subscription: unsubscribe });
  },

  // Unsubscribe from real-time changes
  unsubscribeFromChanges: () => {
    const { subscription } = get();
    if (subscription) {
      subscription();
      set({ subscription: null });
    }
  },

  // Force refresh (invalidate cache)
  invalidateCache: async () => {
    return get()?.fetchContent();
  },

  // Clear all data
  clearContent: () => {
    set({ 
      contentItems: [], 
      loading: false, 
      error: null,
      lastUpdated: null
    });
  }
}));

export default useContentStore;